import { cva as cvaFn } from "class-variance-authority";
import { twMerge } from "tailwind-merge";

import { cssVars, IVarsGenOptions, IVarsGenReturn, ICssVars } from "./cssVars";

type ICvaFnOptions<CvaType> = {
  base?: Parameters<typeof cvaFn<CvaType>>[0];
} & Parameters<typeof cvaFn<CvaType>>[1];

type ICvaFnReturn<CvaType> = ReturnType<typeof cvaFn<CvaType>>;

type IOptions<CssVarsType extends ICssVars, CvaType> = ICvaFnOptions<CvaType> &
  IVarsGenOptions<CssVarsType>;

type IReturn<CssVarTypes extends ICssVars, CvaType> = {
  cls: ICvaFnReturn<CvaType>;
  vars: IVarsGenReturn<CssVarTypes>;
};

/**
 * Tailwind (Slate) Variants
 *
 * It's a merge of class-variance-authority and Slate's cssVars.
 *
 * Difference from cva:
 * - uses a `base` prop instead of the first argument
 * - returns a `cls` and `vars` object instead of a function
 *
 * It has a Slate extension that allows for computing css vars from the theme.
 *
 * **Enabling intelisense** -
 * [instructions](https://www.tailwind-variants.org/docs/getting-started#intellisense-setup-optional)
 *
 * @example
 * ```ts
 * const container = tsv({
 *   vars: {
 *     size: "component-size",
 *   },
 *   defaultVarsValues: {
 *     size: "40px",
 *   },
 *   base: "bg-red-500 w-(--component-size) h-(--component-size)",
 *   variants: {
 *     canSee: {
 *       true: "bg-blue-500",
 *     },
 *     color: {
 *       primary: "text-red-500",
 *       secondary: "text-blue-500",
 *     },
 *   },
 *   defaultVariants: {
 *     color: "primary",
 *   },
 * });
 *
 * const containerCls = container.cls();
 *
 * const containerVars = container.vars({
 *   size: "60px",
 * });
 *
 * <div className={containerCls} style={containerVars}>
 *   Hello
 * </div>
 * ```
 */
export function tsv<
  CssVarTypes extends ICssVars,
  CvaType = Record<string, never>,
>(options: IOptions<CssVarTypes, CvaType>): IReturn<CssVarTypes, CvaType> {
  const { base, vars, defaultVarsValues, ...cvaArgs } = options;
  const cls = cvaFn(base, {
    compoundVariants: cvaArgs.compoundVariants,
    defaultVariants: cvaArgs.defaultVariants,
    variants: cvaArgs.variants,
  } as Parameters<typeof cvaFn<CvaType>>[1]);
  const varsFn = cssVars({ vars, defaultVarsValues });
  const clsFn = (...args: Parameters<typeof cls>) => twMerge(cls(...args));

  return { cls: clsFn, vars: varsFn };
}
